import { supabase } from './supabase.js';
import { GROOVES_PHONE_GATE_USER_MESSAGE, profileGroovesBalance } from '../marketplace/constants/groovesEconomy.js';

export { GROOVES_PHONE_GATE_USER_MESSAGE };

const BASE_SAVED_FILTERS = 5;

/** @returns {Promise<Record<string, { cost: number, metadata: object, descripcion: string|null }>>} */
export async function fetchGroovesSpendCosts() {
  const { data, error } = await supabase
    .from('grooves_config')
    .select('reason, grooves, metadata, descripcion')
    .eq('config_kind', 'spend')
    .eq('activo', true);
  if (error) {
    console.warn('[groovesSpendService] fetch costs:', error.message);
    return {};
  }
  const out = {};
  for (const row of data ?? []) {
    out[row.reason] = {
      cost: Math.max(0, Number(row.grooves) || 0),
      metadata: row.metadata && typeof row.metadata === 'object' ? row.metadata : {},
      descripcion: row.descripcion ?? null,
    };
  }
  return out;
}

export function maxSavedFiltersForProfile(profile) {
  const extra = Math.max(0, Number(profile?.extra_saved_filter_slots) || 0);
  return BASE_SAVED_FILTERS + extra;
}

/**
 * @param {string} listingId
 * @returns {Promise<{ ok: boolean, error?: string, message?: string, featured_until?: string, grooves_balance?: number }>}
 */
export async function destacarListingGrooves(listingId) {
  const { data, error } = await supabase.rpc('destacar_listing_grooves', {
    p_listing_id: listingId,
  });
  if (error) {
    return { ok: false, error: 'rpc_failed', message: error.message };
  }
  const row = data ?? {};
  if (!row.ok) {
    return {
      ok: false,
      error: row.code ?? 'failed',
      message: row.message ?? 'No se pudo destacar el anuncio.',
    };
  }
  return {
    ok: true,
    featured_until: row.featured_until,
    grooves_balance: row.grooves_balance,
    idempotent: !!row.idempotent,
  };
}

/**
 * @returns {Promise<{ ok: boolean, error?: string, message?: string, extra_slots?: number, grooves_balance?: number }>}
 */
export async function purchaseExtraFilterSlotGrooves() {
  const { data, error } = await supabase.rpc('purchase_extra_filter_slot_grooves');
  if (error) {
    return { ok: false, error: 'rpc_failed', message: error.message };
  }
  const row = data ?? {};
  if (!row.ok) {
    return {
      ok: false,
      error: row.code ?? 'failed',
      message: row.message ?? 'No se pudo comprar el slot extra.',
    };
  }
  return {
    ok: true,
    extra_slots: row.extra_slots,
    grooves_balance: row.grooves_balance,
    idempotent: !!row.idempotent,
  };
}

export function canAffordSpend(balance, cost) {
  return profileGroovesBalance({ grooves: balance, grooves_total: balance }) >= Math.max(0, Number(cost) || 0);
}

/** @returns {Promise<Array<object>>} */
export async function fetchCosmeticBadgesCatalog() {
  const [catRes, costs] = await Promise.all([
    supabase.rpc('list_cosmetic_badges_catalog'),
    fetchGroovesSpendCosts(),
  ]);
  if (catRes.error) {
    console.warn('[groovesSpendService] catalog:', catRes.error.message);
    return [];
  }
  return (catRes.data ?? []).map(row => ({
    ...row,
    cost: costs[row.config_reason]?.cost ?? 0,
  }));
}

/**
 * @param {string} profileId
 * @returns {Promise<{ equipped: Array, owned: Array }>}
 */
export async function fetchProfileCosmeticBadges(profileId) {
  const { data, error } = await supabase.rpc('list_profile_cosmetic_badges_public', {
    p_profile_id: profileId,
  });
  if (error) {
    console.warn('[groovesSpendService] profile badges:', error.message);
    return { equipped: [], owned: [] };
  }
  const row = data ?? {};
  return {
    equipped: Array.isArray(row.equipped) ? row.equipped : [],
    owned: Array.isArray(row.owned) ? row.owned : [],
  };
}

/**
 * @param {string} badgeSlug
 */
export async function purchaseCosmeticBadgeGrooves(badgeSlug) {
  const { data, error } = await supabase.rpc('purchase_cosmetic_badge', { p_badge_slug: badgeSlug });
  if (error) return { ok: false, error: 'rpc_failed', message: error.message };
  const row = data ?? {};
  if (!row.ok) {
    return { ok: false, error: row.code, message: row.message ?? 'No se pudo comprar.' };
  }
  return {
    ok: true,
    badge_slug: row.badge_slug,
    label: row.label,
    grooves_balance: row.grooves_balance,
    cost: row.cost,
  };
}

export async function setCosmeticBadgeEquipped(badgeSlug, equipped) {
  const { data, error } = await supabase.rpc('set_cosmetic_badge_equipped', {
    p_badge_slug: badgeSlug,
    p_equipped: !!equipped,
  });
  if (error) return { ok: false, message: error.message };
  const row = data ?? {};
  if (!row.ok) return { ok: false, error: row.code, message: row.message };
  return { ok: true, equipped: row.equipped ?? [] };
}

export async function purchaseSellerStats30d() {
  const { data, error } = await supabase.rpc('purchase_seller_stats_30d');
  if (error) return { ok: false, error: 'rpc_failed', message: error.message };
  const row = data ?? {};
  if (!row.ok) return { ok: false, error: row.code, message: row.message ?? 'Error.' };
  return {
    ok: true,
    seller_stats_until: row.seller_stats_until,
    grooves_balance: row.grooves_balance,
    cost: row.cost,
  };
}

export async function fetchSellerPremiumStats() {
  const { data, error } = await supabase.rpc('get_seller_premium_stats');
  if (error) return { ok: false, error: 'rpc_failed', message: error.message };
  const row = data ?? {};
  if (!row.ok) return { ok: false, error: row.code, message: row.message };
  return { ok: true, ...row };
}
